// Minimal HTML allowlist sanitizer for product description rendering.
//
// The description column stores HTML authored by internal staff via a
// rich-text editor — not arbitrary user input — but we still strip every
// non-allowlisted tag, attribute, and protocol before injecting via
// dangerouslySetInnerHTML. Belt-and-suspenders defense in case a future
// import path widens the trust boundary.
//
// Uses the browser's DOMParser (no extra dep). If you ever need this on
// the server, swap to `linkedom` or `jsdom` and keep the same allowlist.

const ALLOWED_TAGS = new Set([
  'p',
  'br',
  'strong',
  'b',
  'em',
  'i',
  'u',
  'ul',
  'ol',
  'li',
  'a',
  'span',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'blockquote',
  'code',
  'pre',
]);

// Per-tag attribute allowlist. Any attribute not listed is dropped — this
// also blocks `on*` event handlers and inline `style` (we render inside
// our own typography styles).
const ALLOWED_ATTRS: Record<string, ReadonlySet<string>> = {
  a: new Set(['href', 'title', 'target', 'rel']),
};

// Only http(s) and mailto links survive — drops javascript:, data:, etc.
const SAFE_HREF_RE = /^(https?:|mailto:)/i;

const sanitizeNode = (node: Element): void => {
  // Walk children first, snapshot to an array (NodeList is live).
  for (const child of Array.from(node.children)) {
    sanitizeNode(child);
  }

  const tag = node.tagName.toLowerCase();
  if (!ALLOWED_TAGS.has(tag)) {
    // Replace the disallowed element with its text content so nested
    // text doesn't disappear (matches what most rich-text renderers do
    // when they encounter unknown markup).
    const textNode = node.ownerDocument.createTextNode(node.textContent ?? '');
    node.replaceWith(textNode);
    return;
  }

  const allowed = ALLOWED_ATTRS[tag] ?? new Set<string>();
  for (const attr of Array.from(node.attributes)) {
    if (!allowed.has(attr.name)) {
      node.removeAttribute(attr.name);
      continue;
    }
    if (attr.name === 'href' && !SAFE_HREF_RE.test(attr.value)) {
      node.removeAttribute(attr.name);
    }
  }

  // Force external links to open safely.
  if (tag === 'a' && node.hasAttribute('target')) {
    node.setAttribute('rel', 'noopener noreferrer');
  }
};

export const sanitizeHtml = (raw: string | null | undefined): string => {
  if (!raw) return '';
  if (typeof DOMParser === 'undefined') {
    // SSR / non-browser env — drop all tags as a safe fallback. This path
    // should not run in production for the detail page (CSR-only route).
    return raw.replace(/<[^>]*>/g, '');
  }
  const doc = new DOMParser().parseFromString(`<div>${raw}</div>`, 'text/html');
  const root = doc.body.firstElementChild;
  if (!root) return '';
  for (const child of Array.from(root.children)) {
    sanitizeNode(child);
  }
  return root.innerHTML;
};
